import { readSession } from './components/AuthContext';

/**
 * Standardized API client for all backend calls.
 * Automatically injects the correct Base URL based on current subdomain.
 * Automatically injects X-Branch-Id header for branch context.
 */
export const apiFetch = async (endpoint, options = {}) => {
    // Determine Base URL dynamically based on environment and current subdomain
    const hostname = window.location.hostname;
    let protocol = process.env.REACT_APP_API_PROTOCOL || 'http';
    let port = process.env.REACT_APP_API_PORT ? `:${process.env.REACT_APP_API_PORT}` : '';
    
    // Dynamic local fallback if accessing via localhost
    let baseUrl;
    if (hostname.includes('localhost')) {
        const baseDomain = process.env.REACT_APP_BASE_DOMAIN || 'esensevvg.online';
        protocol = process.env.REACT_APP_API_PROTOCOL || 'https';
        port = process.env.REACT_APP_API_PORT ? `:${process.env.REACT_APP_API_PORT}` : '';
        baseUrl = `${protocol}://${baseDomain}${port}`;
    } else {
        baseUrl = `${protocol}://${hostname}${port}`;
    }
    
    // Merge headers
    const headers = {
        'Accept': 'application/json',
        ...(options.headers || {})
    };

    // Only set Content-Type if not sending FormData (browser handles boundary)
    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }

    // Inject Branch ID from current user session
    const session = readSession();
    if (session && session.activeBranch) {
        headers['X-Branch-Id'] = session.activeBranch.id;
    } else if (session && session.userType === 'company_admin') {
        headers['X-Branch-Id'] = 'all'; // Head office aggregate view by default
    }

    const config = {
        ...options,
        headers,
        credentials: 'omit' // For token-based or no-cors local dev if needed. Wait, if using cookie auth, we need 'include'
    };

    // We used login via GlobalAuthController which returns an auth object. 
    // We should strictly use token-based if we are completely decoupled, or we don't even need auth yet if the API isn't protected.
    // Tenant APIs are currently inside 'api' prefix but maybe they don't have 'auth:sanctum' middleware applied?
    // Let's check routes/tenant.php. Currently it just serves /api/materials.

    return fetch(`${baseUrl}${endpoint}`, config);
};

/**
 * Returns the base domain (e.g., esensevvg.online or lvh.me)
 */
export const getBaseDomain = () => {
    const hostname = window.location.hostname;
    if (hostname.includes('localhost')) {
        return 'localhost';
    }
    return process.env.REACT_APP_BASE_DOMAIN || 'lvh.me';
};

/**
 * Checks if the current page is being accessed via the central domain
 */
export const isCentralDomain = () => {
    const hostname = window.location.hostname;
    const baseDomain = getBaseDomain();
    
    // It's central if hostname exactly matches base domain OR is localhost
    return hostname === baseDomain || hostname === 'localhost' || hostname === `csmpro.${baseDomain}`;
};

/**
 * Extracts the subdomain from the current hostname
 */
export const getSubdomain = () => {
    const hostname = window.location.hostname;
    const baseDomain = getBaseDomain();
    
    if (isCentralDomain()) return null;
    
    // If hostname is "client.esensevvg.online", split by base domain
    if (hostname.endsWith(`.${baseDomain}`)) {
        return hostname.replace(`.${baseDomain}`, '');
    }
    
    return null;
};

